import { useState } from 'react';
import { useGunsmithStore } from '../store/gunsmithStore';
import { listChassisOptions } from '../catalogue/assets';

const CHASSIS_OPTIONS = listChassisOptions();
const DEFAULT_ARCHETYPES = ['sidearm', 'magnum', 'vector', 'marksman', 'assault', 'longshot'];

/** The weapon roster: pick which weapon is being edited, create a new one. */
export function RosterPanel() {
  const weapons = useGunsmithStore((s) => s.weapons);
  const rosterLoaded = useGunsmithStore((s) => s.rosterLoaded);
  const sourceWeapons = useGunsmithStore((s) => s.sourceWeapons);
  const activeWeaponId = useGunsmithStore((s) => s.activeWeaponId);
  const publishedWeaponIds = useGunsmithStore((s) => s.publishedWeaponIds);
  const setActiveWeaponId = useGunsmithStore((s) => s.setActiveWeaponId);
  const createWeapon = useGunsmithStore((s) => s.createWeapon);
  const duplicateWeapon = useGunsmithStore((s) => s.duplicateWeapon);
  const deleteWeapon = useGunsmithStore((s) => s.deleteWeapon);
  const saveState = useGunsmithStore((s) => s.saveState);
  const busy = saveState === 'saving';

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [archetype, setArchetype] = useState(DEFAULT_ARCHETYPES[0]!);
  const [chassisId, setChassisId] = useState(CHASSIS_OPTIONS[0]?.id ?? '');

  const sourceById = new Map(sourceWeapons.map((w) => [w.id, w]));

  function submitCreate() {
    if (!name.trim() || !chassisId) return;
    createWeapon(name.trim(), archetype, chassisId);
    setName('');
    setCreating(false);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, padding: '10px 10px 14px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, overflowY: 'auto' }}>
        {/* The store's own toolbar shows "Loading your weapons..." while the
            roster fetch is in flight (GunsmithToolbar's statusMessage) - but
            weapons stays [] the whole time, so without this check this list
            said "No weapons yet" at the very same moment, directly
            contradicting the toolbar above it. */}
        {!rosterLoaded ? (
          <p style={{ fontSize: 12, color: 'var(--ss-text-3, #6d747e)', padding: '8px 4px' }}>Loading weapons…</p>
        ) : weapons.length === 0 ? (
          <p style={{ fontSize: 12, color: 'var(--ss-text-3, #6d747e)', padding: '8px 4px' }}>
            No weapons yet. Create one below.
          </p>
        ) : null}
        {weapons.map((weapon) => {
          const active = weapon.id === activeWeaponId;
          const dirty = !sourceById.has(weapon.id);
          const published = publishedWeaponIds.includes(weapon.id);
          return (
            <button
              key={weapon.id}
              type="button"
              onClick={() => setActiveWeaponId(weapon.id)}
              style={{
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid ' + (active ? 'var(--ss-accent-border, #6444d6)' : 'var(--ss-border-soft, rgba(255,255,255,0.08))'),
                background: active ? 'var(--ss-accent-soft, rgba(100,68,214,0.16))' : 'transparent',
                color: active ? 'var(--ss-accent, #6444d6)' : 'var(--ss-text, #dfe3e8)',
              }}
            >
              <span style={{ fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                {weapon.name || 'Untitled weapon'}
                {dirty && <span title="Unsaved" style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--ss-warning, #d0a53f)', flexShrink: 0 }} />}
              </span>
              <span style={{ fontSize: 10.5, color: 'var(--ss-text-3, #6d747e)' }}>
                {weapon.archetype} · {weapon.chassisId}{published ? ' · Published' : ''}
              </span>
            </button>
          );
        })}
      </div>

      {activeWeaponId && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <button type="button" className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => duplicateWeapon(activeWeaponId)} disabled={busy}>
            Duplicate
          </button>
          <button
            type="button"
            className="ss-btn ss-btn--ghost ss-btn--sm"
            onClick={() => void deleteWeapon(activeWeaponId)}
            disabled={busy}
            style={{ color: 'var(--ss-danger, #d0453f)' }}
          >
            Remove
          </button>
        </div>
      )}

      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--ss-border-soft, rgba(255,255,255,0.08))' }}>
        {!creating ? (
          <button type="button" className="ss-btn ss-btn--primary ss-btn--sm" onClick={() => setCreating(true)} style={{ width: '100%' }}>
            New weapon
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input
              className="ss-input"
              placeholder="Weapon name..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              style={{ padding: '7px 10px', fontSize: 12, borderRadius: 8 }}
            />
            <select
              className="ss-input"
              value={archetype}
              onChange={(e) => setArchetype(e.target.value)}
              style={{ padding: '7px 10px', fontSize: 12, borderRadius: 8 }}
            >
              {DEFAULT_ARCHETYPES.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <select
              className="ss-input"
              value={chassisId}
              onChange={(e) => setChassisId(e.target.value)}
              style={{ padding: '7px 10px', fontSize: 12, borderRadius: 8 }}
            >
              {CHASSIS_OPTIONS.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => setCreating(false)} style={{ flex: 1 }}>
                Cancel
              </button>
              <button type="button" className="ss-btn ss-btn--primary ss-btn--sm" onClick={submitCreate} disabled={!name.trim()} style={{ flex: 1 }}>
                Create
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
